import { createApiRequest } from "@/lib/api/createApiService";
import fetchApi from "@/lib/api/fetchApi";
import { MASTER_DATA_ENDPOINTS } from "./master-data.endpoints";

const parseMasterDataResponse = (
  response,
  { throwOnError = true, fallbackValue = null } = {},
) => {
  // Standard API format
  if (response?.status === "success") {
    return response.data ?? response;
  }

  // Non-standard format from these endpoints:
  // { message: "...", data: ... }
  if (
    response &&
    Object.prototype.hasOwnProperty.call(response, "message") &&
    Object.prototype.hasOwnProperty.call(response, "data")
  ) {
    return response.data;
  }

  if (throwOnError) {
    throw new Error(response?.message || "Invalid response format");
  }
  return fallbackValue;
};

export const masterDataService = {
  // Get reporting boundary
  getReportingBoundary: async (options = {}) => {
    return createApiRequest({
      url: MASTER_DATA_ENDPOINTS.BOUNDARIES,
      method: "GET",
      ...options,
    });
  },

  // Get frameworks
  getFrameworks: async (options = {}) => {
    return createApiRequest({
      url: MASTER_DATA_ENDPOINTS.FEATURES,
      method: "GET",
      ...options,
    });
  },

  // List master data
  list: async (params = {}, options = {}) => {
    const { throwOnError = true, headers = {} } = options;

    try {
      const response = await fetchApi.get(
        MASTER_DATA_ENDPOINTS.LIST,
        params,
        headers,
      );
      return parseMasterDataResponse(response, {
        throwOnError,
        fallbackValue: [],
      });
    } catch (error) {
      if (throwOnError) throw error;
      return [];
    }
  },

  // Create
  create: async (data, options = {}) => {
    return createApiRequest({
      url: MASTER_DATA_ENDPOINTS.CREATE,
      method: "POST",
      body: data,
      ...options,
    });
  },

  // Save by selected disclosures
  saveByDisclosures: async (data, options = {}) => {
    const { throwOnError = true, headers = {} } = options;

    try {
      const response = await fetchApi.post(
        MASTER_DATA_ENDPOINTS.CREATE,
        data,
        false,
        headers,
      );
      return parseMasterDataResponse(response, {
        throwOnError,
        fallbackValue: null,
      });
    } catch (error) {
      if (throwOnError) throw error;
      return null;
    }
  },

  cloneByYear: async (data, options = {}) => {
    const { throwOnError = true, headers = {} } = options;

    try {
      const response = await fetchApi.post(
        MASTER_DATA_ENDPOINTS.CLONE_BY_YEAR,
        data,
        false,
        headers,
      );
      return parseMasterDataResponse(response, {
        throwOnError,
        fallbackValue: null,
      });
    } catch (error) {
      if (throwOnError) throw error;
      return null;
    }
  },

  // Update
  update: async (disclosureId, data, options = {}) => {
    return createApiRequest({
      url: MASTER_DATA_ENDPOINTS.UPDATE(disclosureId),
      method: "PUT",
      body: data,
      ...options,
    });
  },

  updateDisclosureById: async (disclosureId, data, options = {}) => {
    const { throwOnError = true, headers = {} } = options;

    try {
      const response = await fetchApi.put(
        MASTER_DATA_ENDPOINTS.UPDATE(disclosureId),
        data,
        false,
        headers,
      );
      return parseMasterDataResponse(response, {
        throwOnError,
        fallbackValue: null,
      });
    } catch (error) {
      if (throwOnError) throw error;
      return null;
    }
  },

  // Delete
  remove: async (disclosureId, options = {}) => {
    const { throwOnError = true, headers = {} } = options;

    try {
      const response = await fetchApi.delete(
        MASTER_DATA_ENDPOINTS.DELETE(disclosureId),
        headers,
      );
      return parseMasterDataResponse(response, {
        throwOnError,
        fallbackValue: null,
      });
    } catch (error) {
      if (throwOnError) throw error;
      return null;
    }
  },

  removeDisclosureById: async (disclosureId, options = {}) => {
    const { throwOnError = true, headers = {} } = options;

    try {
      const response = await fetchApi.delete(
        MASTER_DATA_ENDPOINTS.DELETE(disclosureId),
        headers,
      );
      return parseMasterDataResponse(response, {
        throwOnError,
        fallbackValue: null,
      });
    } catch (error) {
      if (throwOnError) throw error;
      return null;
    }
  },

  removeAllDisclosure: async (disclosureIds, options = {}) => {
    const { throwOnError = true, headers = {} } = options;

    try {
      const response = await fetchApi.post(
        MASTER_DATA_ENDPOINTS.DELETE_MULTIPLE,
        { disclosureIds },
        false,
        { "Content-Type": "application/json", ...headers },
      );
      return parseMasterDataResponse(response, {
        throwOnError,
        fallbackValue: null,
      });
    } catch (error) {
      if (throwOnError) throw error;
      return null;
    }
  },
};
